/**
 * The Explore run being played (spec 2026-11-03): which published level, its
 * difficulty when it was opened, and — when it was started from a chapter —
 * which chapter and which of its levels. Read by GameScene's win screen (the
 * reward, the chapter's progress) and by `levelPickerFor` (back to the
 * chapter rather than the Explore list). Pure: no Phaser, no client.
 */
export interface ExploreRunContext {
  code: string;
  hambis: number | null;
  chapter?: { code: string };
}

let current: ExploreRunContext | null = null;

export function setExploreRun(ctx: ExploreRunContext | null): void {
  current = ctx;
}

export function exploreRun(): ExploreRunContext | null {
  return current;
}

// ── A chapter's progress, per device ─────────────────────────────────────────

/** `{ [chapterCode]: [levelCodes won] }` — device-only, never synced. */
export const CHAPTER_PROGRESS_KEY = 'hambi.chapterProgress';
/** Enough for any player; the oldest chapters drop off past it. */
const MAX_TRACKED_CHAPTERS = 200;

type KeyValue = Pick<Storage, 'getItem' | 'setItem'>;

function readAll(storage: KeyValue): Record<string, string[]> {
  try {
    const raw = storage.getItem(CHAPTER_PROGRESS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(v)) out[k] = v.filter((x): x is string => typeof x === 'string');
    }
    return out;
  } catch {
    return {};
  }
}

/** The level codes this device has won in a chapter. */
export function chapterWins(storage: KeyValue, chapterCode: string): string[] {
  return readAll(storage)[chapterCode] ?? [];
}

/** Record a won level of a chapter (idempotent). */
export function recordChapterWin(storage: KeyValue, chapterCode: string, levelCode: string): void {
  const all = readAll(storage);
  const won = all[chapterCode] ?? [];
  if (won.includes(levelCode)) return;
  delete all[chapterCode];
  all[chapterCode] = [...won, levelCode];
  const keys = Object.keys(all);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_TRACKED_CHAPTERS))) delete all[k];
  try {
    storage.setItem(CHAPTER_PROGRESS_KEY, JSON.stringify(all));
  } catch {
    /* storage full: progress is a convenience */
  }
}
